import { PROVIDER_LABELS } from './modelConstants';
import { isUsableProviderConfig } from './userPageHelpers';

/**
 * Build dropdown options for the user's configured providers: the one list
 * every provider dropdown shows (the left panel, Settings, and the workflow
 * builder's pickers), so all of them name and offer keys the same way.
 *
 * One option per saved config that can serve a model
 * (`isUsableProviderConfig`, the backend run-start check's rule).  A user's
 * second and later keys get a `provider_N` value and a numbered label, e.g.
 * "OpenAI (2) — Work".  Each option carries `provider` (base name) and
 * `configIndex` so callers can store them without parsing the composite
 * value.
 *
 * @param {object} providerConfigs - Raw `provider_configs` from
 *   /api/user/api_keys, keyed by provider name; each value is an array of
 *   config objects with {config_index, config_name, api_key, endpoint, ...}.
 * @returns {Array<{value:string, label:string, provider:string, configIndex:number}>}
 */
export function buildConfiguredProviderOptions(providerConfigs) {
  const opts = [];
  if (!providerConfigs) return opts;
  Object.entries(providerConfigs).forEach(([prov, cfgList]) => {
    if (!Array.isArray(cfgList)) return;
    cfgList
      .filter((cfg) => isUsableProviderConfig(prov, cfg))
      .forEach((cfg) => {
        const idx = cfg.config_index ?? 1;
        const value = idx === 1 ? prov : `${prov}_${idx}`;
        const baseName =
          PROVIDER_LABELS[prov] ||
          prov.charAt(0).toUpperCase() + prov.slice(1);
        const numSuffix = idx > 1 ? ` (${idx})` : '';
        const label = cfg.config_name
          ? `${baseName}${numSuffix} — ${cfg.config_name}`
          : `${baseName}${numSuffix}`;
        opts.push({ value, label, provider: prov, configIndex: idx });
      });
  });
  return opts;
}
